import { useNotification } from '@refinedev/core';
import { Checkbox, Divider, Form, Input, Modal, Typography } from 'antd';
import React, { useCallback, useState } from 'react';

import { useCreateCompany, useGetInfoByUserCode } from '../../../../api/company';
import {
  AutoComplete,
  SmartUidAutoCompleteInput,
} from '../../../../components/inputs/autocomplete';
import { clearFieldErrorMessageOnChange } from '../../../../utils/form.constants';
import { useNotificationOkCancel } from '../../../../utils/hooks/useNotificationOkCancel.hook';
import { ButtonListFooterModalSM } from '../../../components/buttonsList';
import { confirmModalUnsavedChangesProps } from '../../../components/constants';
import {
  companyAddress1Rules,
  companyCityRules,
  companyCountryRules,
  companyDirectorsFirstNameRules,
  companyDirectorsLastNameRules,
  companyEmailRules,
  companyFullNameRules,
  companyNameRules,
  companyPostalCodeRules,
  companyStateProvinceRules,
  companyTinVatUidRules,
} from '../../../payment-request/addCompany.constants';
import { ButtonTransferMode } from './button-transfer-mode';
import { DownOutlined } from '@ant-design/icons';

interface IProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  customerId?: number;
}

export const AddBeneficiaryModal: React.FC<IProps> = ({ open, onClose, onSuccess, customerId }) => {
  console.log('customerId', customerId);
  const [form] = Form.useForm();
  const { open: openNotification } = useNotification();
  const [beneficiaryType, setBeneficiaryType] = useState('NEW');
  const [infoByUserCode, setInfoByUserCode] = useState<any>(null);
  const [disableField, setDisableField] = useState(false);
  const [includeWallet, setIncludeWallet] = useState(false);
  const [includeBank, setIncludeBank] = useState(false);

  const { mutate: fetchInfoByUserCode, isLoading: isLoadingUserCode } = useGetInfoByUserCode({
    onSuccess: (response: { data: { data: any } }) => {
      setInfoByUserCode(response?.data?.data);
      form.setFields([
        {
          name: 'smartUID',
          errors: undefined,
        },
      ]);
    },
    onError: (error: { message: string; reason_code: string }) => {
      setInfoByUserCode(
        error?.reason_code === 'ERR00010'
          ? 'This SMART UID does not exist in SMART+'
          : error?.message
      );
      if (error?.reason_code === 'ERR00010') {
        form.setFields([
          {
            name: 'smartUID',
            errors: ['This SMART UID does not exist in SMART+'],
          },
        ]);
      } else {
        form.setFields([
          {
            name: 'smartUID',
            errors: [error?.message || ''],
          },
        ]);
      }
    },
  });

  const { mutate: createBeneficiary, isLoading: loadingCreateBeneficiary } = useCreateCompany({
    onSuccess: (response: { data: { message: string } }) => {
      if (openNotification) {
        openNotification({
          key: 'create_success',
          message: response.data.message || 'Create Beneficiary successfully!',
          type: 'success',
        });
      }
      form.resetFields();
      setInfoByUserCode(null);
      setDisableField(false);
      setBeneficiaryType('NEW');
      setIncludeWallet(false);
      setIncludeBank(false);
      onClose();
      onSuccess?.();
    },
    onError: (error: { message: string }) => {
      if (openNotification) {
        openNotification({
          key: 'create_failed',
          message: error.message || 'Create Beneficiary failed!',
          type: 'error',
        });
      }
    },
  });

  const handleChangeMethod = (type: string) => {
    setDisableField(false);
    setBeneficiaryType(type);
    setInfoByUserCode(null);
    form?.resetFields();
  };

  const resetSmartUidLinkedFields = useCallback(() => {
    form?.resetFields([
      'name',
      'fullName',
      'directorFirstName',
      'directorLastName',
      'address1',
      'address2',
      'city',
      'stateProvince',
      'country',
      'tinVatUid',
      'regNo',
      'phoneNumber1',
      'email',
      'website',
      'postalCode',
    ]);
  }, [form]);

  const handleSmartUidLookup = useCallback(async (selectedCode?: string) => {
    const code = selectedCode
      ? selectedCode.trim()
      : form.getFieldValue('smartUID')
        ? form.getFieldValue('smartUID')?.trim()
        : '';

    if (!code) {
      setDisableField(false);
      resetSmartUidLinkedFields();
      form.setFields([
        {
          name: 'smartUID',
          errors: ['This field is required'],
        },
      ]);
      setInfoByUserCode(null);
      return;
    }
    resetSmartUidLinkedFields();
    await fetchInfoByUserCode(code);
  }, [fetchInfoByUserCode, form, resetSmartUidLinkedFields]);

  React.useEffect(() => {
    if (
      !infoByUserCode ||
      typeof infoByUserCode === 'string' ||
      beneficiaryType === 'NEW'
    ) {
      setDisableField(false);
      return;
    }
    setDisableField(true);
    form.setFieldsValue({
      fullName: infoByUserCode?.fullName,
      address1: infoByUserCode?.address,
      country: infoByUserCode?.country,
      regNo: infoByUserCode?.regNo,
      email: infoByUserCode?.email,
      website: infoByUserCode?.website,
    });
  }, [infoByUserCode, beneficiaryType, form]);

  const onSubmit = useCallback(
    (values: any) => {
      if (loadingCreateBeneficiary) return;

      const infoBody = {
        fullName: values.fullName || '',
        name: values.name || '',
        directorFirstName: values.directorFirstName || '',
        directorLastName: values.directorLastName || '',
        address1: values.address1 || '',
        address2: values.address2 || '',
        city: values.city || '',
        stateProvince: values.stateProvince || '',
        country: values.country || '',
        tinVatUid: values.tinVatUid || '',
        regNo: values.regNo || '',
        phoneNumber1: values.phoneNumber1 || '',
        email: values.email || '',
        smartUID: values.smartUID ? values.smartUID?.trim() : undefined,
        website: values.website || '',
        postalCode: values.postalCode || '',
        refUserId: infoByUserCode?.id ? infoByUserCode?.id : undefined,
      };

      const companyWallet = includeWallet && values.walletCurrency && values.walletNetwork && values.walletAddress
        ? {
          assetConfigGroupId: values.walletCurrency || 0,
          networkId: values.walletNetwork || 0,
          address: values.walletAddress || '',
          name: values.walletName || '',
        }
        : undefined;

      const bank = includeBank && values.bankName && values.bankSwift
        ? {
          name: values.bankName || '',
          swift: values.bankSwift || '',
          legalAddress: values.bankAddress || '',
          country: values.bankCountry || '',
        }
        : undefined;

      const bankAccount = includeBank && values.accountNumber
        ? {
          ...(values.bankId ? { bankId: values.bankId } : {}),
          currencyCode: values.currencyCode || '',
          abaRoutingNumber: values.abaRoutingNumber || undefined,
          bankStateBranchNumber: values.bankStateBranchNumber || undefined,
          branchNumber: values.branchNumber || '',
          accountNumber: values.accountNumber || '',
        }
        : undefined;

      const companyBody = {
        ...infoBody,
        ...(companyWallet ? { companyWallet } : {}),
        ...(bank ? { bank } : {}),
        ...(bankAccount ? { bankAccount } : {}),
      };

      createBeneficiary(companyBody);
    },
    [loadingCreateBeneficiary, createBeneficiary, infoByUserCode, includeWallet, includeBank]
  );

  const onSave = useCallback(() => {
    form.submit();
  }, [form]);

  const { openNotification: openUnsavedNotification, contextHolder } = useNotificationOkCancel({
    ...confirmModalUnsavedChangesProps,
    onOk: () => {
      form.resetFields();
      setInfoByUserCode(null);
      setDisableField(false);
      setBeneficiaryType('NEW');
      setIncludeWallet(false);
      setIncludeBank(false);
      onClose();
    },
  });

  const onModalCancel = useCallback(() => {
    const formIsNotEmpty = Object.values(form.getFieldsValue()).some(
      (elm) => elm !== undefined && elm !== ''
    );
    if (formIsNotEmpty) {
      openUnsavedNotification();
    } else {
      form.resetFields();
      setInfoByUserCode(null);
      setDisableField(false);
      setBeneficiaryType('NEW');
      setIncludeWallet(false);
      setIncludeBank(false);
      onClose();
    }
  }, [onClose, form, openUnsavedNotification]);

  return (
    <Modal
      open={open}
      title="Add Beneficiary"
      className="smartModalSM"
      width={800}
      onCancel={onModalCancel}
      afterClose={() => {
        form.resetFields();
        setInfoByUserCode(null);
        setDisableField(false);
        setBeneficiaryType('NEW');
        setIncludeWallet(false);
        setIncludeBank(false);
      }}
      footer={ButtonListFooterModalSM(onSave, onModalCancel, loadingCreateBeneficiary || isLoadingUserCode)}
    >
      {contextHolder}
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        disabled={loadingCreateBeneficiary || isLoadingUserCode}
        onValuesChange={clearFieldErrorMessageOnChange(form)}
      >
        <div
          style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.15)', marginBottom: '24px', paddingBottom: '16px' }}
        >
          <div className="transfer-mode-list-button">
            <ButtonTransferMode
              size={'small'}
              active={beneficiaryType === 'NEW'}
              text="New"
              width={'60px'}
              onClick={() => handleChangeMethod('NEW')}
            />
            <ButtonTransferMode
              size={'small'}
              active={beneficiaryType === 'FIND_ON_SMART'}
              text="Find on SMART"
              width={'fit-content'}
              onClick={() => handleChangeMethod('FIND_ON_SMART')}
            />
          </div>
          {beneficiaryType === 'FIND_ON_SMART' ? (
            <Form.Item
              label="SMART UID"
              validateTrigger="onBlur"
              name="smartUID"
              rules={[
                {
                  required: true,
                  message: 'This field is required',
                },
                {
                  max: 256,
                  message: 'Maximum 256 characters',
                  validateTrigger: 'onBlur',
                },
              ]}
            >
              <SmartUidAutoCompleteInput
                placeholder="Enter the code linked to your SMART account (min. 3 characters)"
                onBlur={() => void handleSmartUidLookup()}
                onSuggestionSelect={(value) => void handleSmartUidLookup(value)}
              />
            </Form.Item>
          ) : null}
        </div>

        <Typography.Title level={5}>Company Information</Typography.Title>
        <Form.Item name="name" label="Company Name" rules={companyNameRules}>
          <Input placeholder="Enter the full legal name of the company" disabled={disableField} />
        </Form.Item>
        <Form.Item name="fullName" label="Full Company Name" rules={companyFullNameRules}>
          <Input placeholder="Enter the full legal name of the company" disabled={disableField} />
        </Form.Item>
        <Form.Item
          name="directorFirstName"
          label="Director's First Name"
          rules={companyDirectorsFirstNameRules}
        >
          <Input placeholder="Enter the first name(s) of the company director" />
        </Form.Item>
        <Form.Item
          name="directorLastName"
          label="Director's Last Name"
          rules={companyDirectorsLastNameRules}
        >
          <Input placeholder="Enter the last name (surname) of the company director" />
        </Form.Item>
        <Form.Item name="country" label="Country of incorporation" rules={companyCountryRules}>
          <AutoComplete
            resource="api/v1/worldCheck/countries"
            mapItem={(item: { value: string; label: string }) => ({
              value: item.label,
              key: item.value,
              label: <Typography.Text>{item.label}</Typography.Text>,
              data: item,
            })}
            notFoundPlaceholder="Start by typing country name"
          >
            <Input
              // style={{ fontSize: 16 }}
              placeholder="Select the country where the company is officially registered"
              disabled={disableField}
              suffix={<DownOutlined />}
            />
          </AutoComplete>
        </Form.Item>
        <Form.Item name="address1" label="Address 1" rules={companyAddress1Rules}>
          <Input.TextArea
            placeholder="Enter the primary street address of the company"
            autoSize={{ minRows: 1, maxRows: 5 }}
            disabled={disableField}
          />
        </Form.Item>
        <Form.Item name="address2" label="Address 2">
          <Input.TextArea
            placeholder="Additional address details (e.g., building, floor)"
            autoSize={{ minRows: 1, maxRows: 5 }}
          />
        </Form.Item>
        <Form.Item name="city" label="City" rules={companyCityRules}>
          <Input placeholder="Enter the city where the company is located" />
        </Form.Item>
        <Form.Item name="stateProvince" label="State/Province" rules={companyStateProvinceRules}>
          <Input placeholder="Enter the state or province of company location" />
        </Form.Item>
        <Form.Item name="postalCode" label="Postal Code" rules={companyPostalCodeRules}>
          <Input placeholder="Enter your postal code" />
        </Form.Item>
        <Form.Item name="regNo" label="Reg No">
          <Input placeholder="Enter the official company registration number" disabled={disableField} />
        </Form.Item>
        <Form.Item name="tinVatUid" label="TIN" rules={companyTinVatUidRules}>
          <Input placeholder="Enter the company's Tax Identification Number" />
        </Form.Item>
        <Form.Item name="phoneNumber1" label="Tel">
          <Input placeholder="Enter the official company phone number" />
        </Form.Item>
        <Form.Item name="email" label="Email" rules={companyEmailRules}>
          <Input placeholder="Enter the official company email address" disabled={disableField} />
        </Form.Item>
        <Form.Item name="website" label="Website">
          <Input placeholder="Enter the company's official website URL" disabled={disableField} />
        </Form.Item>

        <Divider />
        <Typography.Title level={5}>Optional: Wallet Address</Typography.Title>
        <Form.Item>
          <Checkbox
            checked={includeWallet}
            onChange={(e) => setIncludeWallet(e.target.checked)}
          >
            Include wallet address
          </Checkbox>
        </Form.Item>
        {includeWallet && (
          <>
            <Form.Item name="walletCurrency" label="Currency">
              <Input placeholder="Currency (to be implemented)" />
            </Form.Item>
            <Form.Item name="walletNetwork" label="Network">
              <Input placeholder="Network (to be implemented)" />
            </Form.Item>
            <Form.Item name="walletAddress" label="Address">
              <Input placeholder="Wallet address" />
            </Form.Item>
            <Form.Item name="walletName" label="Name">
              <Input placeholder="Wallet name" />
            </Form.Item>
          </>
        )}

        <Divider />
        <Typography.Title level={5}>Optional: Bank Account</Typography.Title>
        <Form.Item>
          <Checkbox
            checked={includeBank}
            onChange={(e) => setIncludeBank(e.target.checked)}
          >
            Include bank account
          </Checkbox>
        </Form.Item>
        {includeBank && (
          <>
            <Form.Item name="bankName" label="Bank Name">
              <Input placeholder="Enter bank name" />
            </Form.Item>
            <Form.Item name="bankSwift" label="Bank Swift">
              <Input placeholder="Enter bank SWIFT code" />
            </Form.Item>
            <Form.Item name="bankAddress" label="Bank Address">
              <Input placeholder="Enter bank address" />
            </Form.Item>
            <Form.Item name="bankCountry" label="Bank Country">
              <AutoComplete
                resource="api/v1/worldCheck/countries"
                mapItem={(item: { value: string; label: string }) => ({
                  value: item.label,
                  key: item.value,
                  label: <Typography.Text>{item.label}</Typography.Text>,
                  data: item,
                })}
                notFoundPlaceholder="Start by typing country name"
              >
                <Input placeholder="Select bank country" suffix={<DownOutlined />} />
              </AutoComplete>
            </Form.Item>
            <Form.Item name="accountNumber" label="Account Number">
              <Input placeholder="Enter account number" />
            </Form.Item>
            <Form.Item name="currencyCode" label="Currency">
              <Input placeholder="Enter currency code" />
            </Form.Item>
            <Form.Item name="branchNumber" label="Branch Number">
              <Input placeholder="Enter branch number" />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};

